import { Suspense } from 'react';
import type { ComponentType, LazyExoticComponent, ReactElement } from 'react';
import { createBrowserRouter, Navigate, useParams } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { PanelGuard } from '@favelafood/shared/auth/PanelGuard';
import { homeOrigin } from '@favelafood/shared/auth/panel';
import { safeInternalPath } from '@favelafood/shared/auth/safeInternalPath';
import { Shell } from './components/layout/Shell';
import App from './App';
import { lazyWithReload } from '@favelafood/shared/lib/lazyWithReload';

// G5 — Login
const Login = lazyWithReload(() => import('./features/login/LoginPage'));

// The staff area carved out to apps/staff (C2) and the owner + KDS area to
// apps/comerciante (C3). Their routes, lazy imports, and MerchantRoute/
// StaffRoute guards were removed here in the same commits — no orphans. The
// admin panel is now 100% admin.

// G6 — Features simples (todas implementadas)
const Dashboard = lazyWithReload(() => import('./features/dashboard/DashboardPage'));
const Orders = lazyWithReload(() => import('./features/orders/OrdersPage'));
const CreateOrder = lazyWithReload(() => import('./features/orders/CreateOrderPage'));
const OrderDetail = lazyWithReload(() => import('./features/orders/OrderDetailPage'));
const Merchants = lazyWithReload(() => import('./features/merchants/MerchantsPage'));
const MerchantDetail = lazyWithReload(() => import('./features/merchants/MerchantDetailPage'));
const Couriers = lazyWithReload(() => import('./features/couriers/CouriersPage'));
const Users = lazyWithReload(() => import('./features/users/UsersPage'));
const Banners = lazyWithReload(() => import('./features/banners/BannersPage'));
const Categories = lazyWithReload(() => import('./features/categories/CategoriesPage'));
const Allergens = lazyWithReload(() => import('./features/allergens/AllergensPage'));
const ErrorLogs = lazyWithReload(() => import('./features/error-logs/ErrorLogsPage'));
const ErrorLogDetail = lazyWithReload(() => import('./features/error-logs/ErrorLogDetailPage'));
const Monitoring = lazyWithReload(() => import('./features/monitoring/MonitoringPage'));
const PaymentsSandbox = lazyWithReload(() => import('./features/payments-sandbox/PaymentsSandboxPage'));
const Settings = lazyWithReload(() => import('./features/settings/SettingsPage'));

// G7 — Features médias (todas implementadas)
const Cadastros = lazyWithReload(() => import('./features/registrations/RegistrationsPage'));
const MapPage = lazyWithReload(() => import('./features/map/MapPage'));
const CourierOps = lazyWithReload(() => import('./features/courier-ops/CourierOpsPage'));
const CustomerOps = lazyWithReload(() => import('./features/customer-ops/CustomerOpsPage'));
const ChangeRequests = lazyWithReload(
  () => import('./features/change-requests/ChangeRequestsPage'),
);

// G8 — Features grandes (todas implementadas)
const MerchantOps = lazyWithReload(() => import('./features/merchant-ops/MerchantOpsLayout'));
const Notifications = lazyWithReload(() => import('./features/notifications/NotificationsLayout'));
const Entities = lazyWithReload(() => import('./features/entities/EntitiesPage'));
const EntityList = lazyWithReload(() => import('./features/entities/EntityListPage'));
const EntityDetail = lazyWithReload(() => import('./features/entities/EntityDetailPage'));

// G9 — Storage
const Storage = lazyWithReload(() => import('./features/storage/StoragePage'));

// G10 — Financeiro (ADM-29..33)
const FeeConfig = lazyWithReload(() => import('./features/finance/FeeConfigPage'));
const Anticipation = lazyWithReload(() => import('./features/finance/AnticipationPage'));
const DeliveryFeeTiers = lazyWithReload(() => import('./features/finance/DeliveryFeeTiersPage'));
const Finance = lazyWithReload(() => import('./features/finance/FinancePage'));
const Campaigns = lazyWithReload(() => import('./features/campaigns/CampaignsPage'));

/**
 * Wraps a lazy-loaded page in `<Suspense>` so the router can render
 * a fallback while the chunk is fetched.
 */
const lazyRoute = (Comp: LazyExoticComponent<ComponentType>): ReactElement => (
  <Suspense fallback={<div className="p-8 text-muted">Carregando...</div>}>
    <Comp />
  </Suspense>
);

/**
 * Onboarding is a STAFF function (FAV-153) and lives on the staff subdomain now.
 * Legacy `/admin/onboarding*` bookmarks bounce to `staff.<base>/<path>`. In
 * localhost `homeOrigin` returns null (dev is per-port), so we fall back to the
 * admin dashboard.
 */
function StaffOnboardingRedirect({ path }: { path: string }): ReactElement {
  const origin = homeOrigin('staff', window.location);
  if (origin) {
    // `path` may embed a URL param (user input): constrain to an internal path.
    window.location.replace(origin + safeInternalPath(path, '/'));
    return <div className="p-8 text-muted">Redirecionando...</div>;
  }
  return <Navigate to="/dashboard" replace />;
}

/** `/onboarding/:id` → staff `/comercios/:id` (cross-subdomain). */
function OnboardingDetailRedirect(): ReactElement {
  const { id } = useParams();
  return <StaffOnboardingRedirect path={`/comercios/${id ?? ''}`} />;
}

/**
 * Browser router for the admin panel.
 *
 * The admin app is served at the root of its own subdomain (base `/`):
 * - `/login` is a public top-level route (no shell).
 * - `/` is protected by `PanelGuard(['admin'])`, renders the shell, and exposes
 *   the 18 feature routes as children with `dashboard` as the default.
 * - Any other URL falls back to the dashboard.
 */
export const routeConfig: RouteObject[] = [
  {
    element: <App />,
    children: [
      { path: '/login', element: lazyRoute(Login) },

      // The owner + KDS `/merchant*` tree carved out to apps/comerciante (C3):
      // the store owner and the kitchen kiosk now log in on their own subdomain
      // (OIDC `favelafood-comerciante`). No `/merchant*` routes live here.
      {
        path: '/',
        element: (
          <PanelGuard allowed={['admin']}>
            <Shell />
          </PanelGuard>
        ),
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: 'dashboard', element: lazyRoute(Dashboard) },
          { path: 'orders', element: lazyRoute(Orders) },
          // `orders/new` must come BEFORE `orders/:id` so the literal "new"
          // segment isn't captured by the dynamic id matcher.
          { path: 'orders/new', element: lazyRoute(CreateOrder) },
          { path: 'orders/:id', element: lazyRoute(OrderDetail) },
          { path: 'merchants', element: lazyRoute(Merchants) },
          { path: 'merchants/:id', element: lazyRoute(MerchantDetail) },
          // Onboarding is a STAFF function (FAV-153): staff lives on its own
          // subdomain (apps/staff). These legacy `/onboarding*` URLs bounce
          // cross-subdomain to the staff panel (localhost falls back to dashboard).
          { path: 'onboarding', element: <StaffOnboardingRedirect path="/novo" /> },
          {
            path: 'onboarding/novo-comerciante',
            element: <StaffOnboardingRedirect path="/novo-comerciante" />,
          },
          {
            path: 'onboarding/novo-comercio',
            element: <StaffOnboardingRedirect path="/novo-comercio" />,
          },
          { path: 'onboarding/:id', element: <OnboardingDetailRedirect /> },
          { path: 'couriers', element: lazyRoute(Couriers) },
          { path: 'users', element: lazyRoute(Users) },
          { path: 'banners', element: lazyRoute(Banners) },
          { path: 'cadastros', element: lazyRoute(Cadastros) },
          { path: 'categories', element: lazyRoute(Categories) },
          { path: 'allergens', element: lazyRoute(Allergens) },
          { path: 'map', element: lazyRoute(MapPage) },
          { path: 'merchant-ops', element: lazyRoute(MerchantOps) },
          { path: 'courier-ops', element: lazyRoute(CourierOps) },
          { path: 'change-requests', element: lazyRoute(ChangeRequests) },
          { path: 'customer-ops', element: lazyRoute(CustomerOps) },
          { path: 'entities', element: lazyRoute(Entities) },
          { path: 'entities/:name', element: lazyRoute(EntityList) },
          { path: 'entities/:name/:id', element: lazyRoute(EntityDetail) },
          { path: 'notifications', element: lazyRoute(Notifications) },
          { path: 'error-logs', element: lazyRoute(ErrorLogs) },
          { path: 'error-logs/:id', element: lazyRoute(ErrorLogDetail) },
          { path: 'monitoring', element: lazyRoute(Monitoring) },
          { path: 'payments-sandbox', element: lazyRoute(PaymentsSandbox) },
          { path: 'storage', element: lazyRoute(Storage) },
          { path: 'settings', element: lazyRoute(Settings) },
          { path: 'finance', element: lazyRoute(Finance) },
          { path: 'finance/fees', element: lazyRoute(FeeConfig) },
          { path: 'finance/anticipation', element: lazyRoute(Anticipation) },
          { path: 'finance/delivery-fees', element: lazyRoute(DeliveryFeeTiers) },
          { path: 'campaigns', element: lazyRoute(Campaigns) },
        ],
      },
      // Catch-all: land the session on the admin dashboard. Wrong-role sessions
      // are handled by PanelGuard (cross-subdomain redirect / deny).
      { path: '*', element: <Navigate to="/dashboard" replace /> },
    ],
  },
];

export const router = createBrowserRouter(routeConfig);
